import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

export default function MaterialRequisitionPrint() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [co, setCo] = useState(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("mrm_token") : null;

  useEffect(() => {
    api.get(`/mrn/${id}`).then(({data}) => setM(data));
    api.get("/settings/company").then(({data}) => setCo(data)).catch(()=>{});
  }, [id]);

  if (!m) return <div className="p-10 text-center text-slate-400">Loading...</div>;

  const isApproved = m.status === "Approved";
  const val = (v) => (v && String(v).trim()) || "";

  // Supplier is ALWAYS the company (MRM) — permanent
  const supplier = {
    name: co?.company_name || "MRM RENEWABLES ENERGY PRIVATE LIMITED",
    address: co?.address || "",
    phone: co?.phone || "",
    email: co?.email || "",
    cin: co?.cin || "",
    udyam: co?.udyam || "",
    gstin: co?.gstin || "",
  };

  return (
    <div className="min-h-screen bg-slate-100 py-6 tally-print">
      <div className="no-print max-w-4xl mx-auto flex justify-between items-center mb-4 px-4">
        <Button variant="outline" onClick={()=>nav(-1)}><ArrowLeft className="w-4 h-4 mr-1"/>Back</Button>
        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-600 bg-white border border-slate-200 rounded-lg px-2 py-1 font-semibold">
            Status: <span className={isApproved?"text-emerald-700":m.status==="Rejected"?"text-rose-700":"text-amber-700"}>{m.status}</span>
          </div>
          <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={()=>window.print()} data-testid="print-mrn-btn"><Printer className="w-4 h-4 mr-1"/>Print / Save PDF</Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto bg-white shadow-lg print-page p-6" data-testid="mrn-doc" style={{fontFamily:'Arial, sans-serif', fontSize:12}}>
        <h1 className="text-center text-lg font-bold mb-3">Approved Material Requisition Note</h1>

        {/* Header table — Supplier (permanent MRM) + MR meta */}
        <table className="w-full border-collapse border border-slate-800 text-[12px]">
          <tbody>
            <tr>
              <td rowSpan={4} className="border border-slate-800 p-2 w-1/2 align-top">
                <div className="font-bold mb-1">Supplier Details</div>
                <div className="font-bold uppercase leading-snug">{supplier.name}</div>
                <div className="whitespace-pre-wrap leading-snug">{supplier.address}</div>
                {supplier.phone && <div>Contact No. +91 {supplier.phone}</div>}
                {supplier.email && <div>e-mail Id : {supplier.email}</div>}
                {supplier.cin && <div>CIN : <span className="font-mono">{supplier.cin}</span></div>}
                {supplier.udyam && <div>Udyam No :<span className="font-mono">{supplier.udyam}</span></div>}
                {supplier.gstin && <div className="font-mono">{supplier.gstin}</div>}
              </td>
              <td className="border border-slate-800 p-2 font-semibold w-1/6">MR No.</td>
              <td className="border border-slate-800 p-2 font-mono">{val(m.mr_no)}</td>
            </tr>
            <tr>
              <td className="border border-slate-800 p-2 font-semibold">Date</td>
              <td className="border border-slate-800 p-2">{fmtDate(m.date || m.created_at)}</td>
            </tr>
            <tr>
              <td className="border border-slate-800 p-2 font-semibold">Customer Order no</td>
              <td className="border border-slate-800 p-2 font-mono">{val(m.customer_order_no)}</td>
            </tr>
            <tr>
              <td className="border border-slate-800 p-2 font-semibold">&nbsp;</td>
              <td className="border border-slate-800 p-2">&nbsp;</td>
            </tr>
            <tr>
              <td className="border border-slate-800 p-2 font-semibold">Contact Person</td>
              <td className="border border-slate-800 p-2">{val(m.bill_person_name) || val(m.contact_person)}</td>
              <td className="border border-slate-800 p-2 font-semibold">Transportation Terms</td>
              <td className="border border-slate-800 p-2">{val(m.transportation_terms)}</td>
            </tr>
            <tr>
              <td className="border border-slate-800 p-2 font-semibold">Contact Number</td>
              <td className="border border-slate-800 p-2 font-mono">{val(m.bill_person_contact) || val(m.contact_number) || val(m.bill_to_mobile)}</td>
              <td className="border border-slate-800 p-2 font-semibold">Payment terms</td>
              <td className="border border-slate-800 p-2">{val(m.payment_terms)}</td>
            </tr>
            <tr>
              <td className="border border-slate-800 p-2 font-semibold">Customer PO No</td>
              <td className="border border-slate-800 p-2 font-mono">{val(m.customer_po_no)}</td>
              <td className="border border-slate-800 p-2 font-semibold">Packaging Type</td>
              <td className="border border-slate-800 p-2">{val(m.packaging_type)}</td>
            </tr>
            <tr>
              <td className="border border-slate-800 p-2 font-semibold">Customer PO Date</td>
              <td className="border border-slate-800 p-2">{fmtDate(m.customer_po_date)}</td>
              <td className="border border-slate-800 p-2 font-semibold">Tentative Dispatch date</td>
              <td className="border border-slate-800 p-2">{fmtDate(m.tentative_dispatch_date)}</td>
            </tr>
          </tbody>
        </table>

        {/* Bill / Ship To — CUSTOMER details */}
        <table className="w-full border-collapse border border-slate-800 border-t-0 text-[12px]">
          <tbody>
            <tr>
              <td className="border border-slate-800 p-2 w-1/2 align-top">
                <div className="font-bold mb-1">Bill To</div>
                <div className="font-semibold uppercase">{val(m.bill_to_name)}</div>
                <div className="whitespace-pre-wrap leading-snug">{val(m.bill_to_address)}</div>
                {val(m.bill_to_mobile) && <div>Contact No. +91 {val(m.bill_to_mobile)}</div>}
                {val(m.bill_to_gstin) && <div>GSTIN: <span className="font-mono">{val(m.bill_to_gstin)}</span></div>}
              </td>
              <td className="border border-slate-800 p-2 align-top">
                <div className="font-bold mb-1">Ship To</div>
                <div className="font-semibold uppercase">{val(m.ship_to_name)}</div>
                <div className="whitespace-pre-wrap leading-snug">{val(m.ship_to_address)}</div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Person contact row */}
        <table className="w-full border-collapse border border-slate-800 border-t-0 text-[12px]">
          <tbody>
            <tr>
              <td className="border border-slate-800 p-2 font-semibold w-1/6">Person Name</td>
              <td className="border border-slate-800 p-2">{val(m.bill_person_name)}</td>
              <td className="border border-slate-800 p-2 font-semibold w-1/6">Contact No</td>
              <td className="border border-slate-800 p-2 font-mono">{val(m.bill_person_contact)}</td>
              <td className="border border-slate-800 p-2 font-semibold w-1/6">Person Name</td>
              <td className="border border-slate-800 p-2">{val(m.ship_person_name)}</td>
              <td className="border border-slate-800 p-2 font-semibold w-1/6">Contact No</td>
              <td className="border border-slate-800 p-2 font-mono">{val(m.ship_person_contact)}</td>
            </tr>
          </tbody>
        </table>

        {/* Items table */}
        <table className="w-full border-collapse border border-slate-800 border-t-0 text-[12px]">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-slate-800 p-2 w-10">Sr No</th>
              <th className="border border-slate-800 p-2 text-left">Product Description</th>
              <th className="border border-slate-800 p-2 w-24">Requested Qty</th>
              <th className="border border-slate-800 p-2 w-24">Approved Qty</th>
              <th className="border border-slate-800 p-2 w-16">Unit</th>
            </tr>
          </thead>
          <tbody>
            {(m.items||[]).map((it,i)=>(
              <tr key={i}>
                <td className="border border-slate-800 p-2 text-center font-mono">{it.sr_no || i+1}</td>
                <td className="border border-slate-800 p-2">{it.product_description}</td>
                <td className="border border-slate-800 p-2 text-right font-mono">{it.requested_qty}</td>
                <td className="border border-slate-800 p-2 text-right font-mono font-bold">{isApproved ? (it.approved_qty ?? it.requested_qty) : "—"}</td>
                <td className="border border-slate-800 p-2 text-center">{it.unit || "Nos"}</td>
              </tr>
            ))}
            {Array.from({length:Math.max(0, 3-(m.items||[]).length)}).map((_,i)=>(
              <tr key={`f${i}`}>
                <td className="border border-slate-800 p-4">&nbsp;</td>
                <td className="border border-slate-800"></td>
                <td className="border border-slate-800"></td>
                <td className="border border-slate-800"></td>
                <td className="border border-slate-800"></td>
              </tr>
            ))}
          </tbody>
        </table>

        {m.notes && (
          <div className="mt-3 text-xs border border-slate-800 p-2 border-t-0">
            <span className="font-semibold">Notes:</span> {m.notes}
          </div>
        )}

        {/* Approval block */}
        <div className="grid grid-cols-2 gap-4 mt-6 text-[12px]">
          <div>
            <div className="text-slate-500">Requested By</div>
            <div className="mt-6 border-t border-slate-800 pt-1 font-semibold">{m.created_by_name || "-"}</div>
            <div className="text-[10px] text-slate-500">{fmtDate(m.created_at)}</div>
          </div>
          <div className="text-right">
            <div className="text-slate-500">Approved By (Admin)</div>
            <div className="mt-6 border-t border-slate-800 pt-1 font-semibold">{m.approved_by_name || (isApproved?"-":"— Pending —")}</div>
            <div className="text-[10px] text-slate-500">{m.approved_at ? fmtDate(m.approved_at) : ""}</div>
            {m.approval_remark && <div className="text-[10px] italic text-slate-600">Remark: {m.approval_remark}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
